import { isNil, round } from "lodash";
import {
    AdjustmentOperation,
    AdjustmentType,
    DemographicPrice,
    Experience,
    LineItemTemplate,
    PricingTerm,
    ProductCatalogItem,
    UnitType,
} from "types";
import { getCatalogItemsByUnitType } from "./Catalog";

/**
 * This function will add the catalogItem model to DemographicPrice object.
 *
 * this will loop over all catalog items in an experience and check if pricing term already has a
 * demographic price object for this catalog item. if exists will add catalogItem object in it. else
 * generate a default item
 *
 * This is required since we need to dynamically show all catalogitems in pricing editors, whenever we add or
 * remove an catalog item from experience even though it is not saved.
 */
export const getDemographicPricesForExperienceCatalog = (
    experience: Experience,
    pricingTerm: PricingTerm,
): DemographicPrice[] => {
    const catalogItems: ProductCatalogItem[] = getCatalogItemsByUnitType(
        experience.catalog?.items ?? [],
        UnitType.DEMOGRAPHIC,
    ).filter((catalogItem) => Boolean(catalogItem.template?.id));
    const demographicPrices: DemographicPrice[] = [];

    for (const catalogItem of catalogItems) {
        const demographicPriceItem = pricingTerm?.demographicPrices?.find(
            (item) => item.template?.id === catalogItem.template?.id,
        );
        const updatedDemographicPriceItem = demographicPriceItem
            ? { ...demographicPriceItem, catalogItem }
            : { catalogItem, template: catalogItem.template! };
        demographicPrices.push(updatedDemographicPriceItem);
    }

    return demographicPrices;
};

/**
 * This function will retuns the demographic Price for a lineItem in a
 * pricing term
 */
export const getDemographicPriceForLineItemTemplate = (
    pricingTerm: PricingTerm,
    lineItemTemplate: LineItemTemplate,
): DemographicPrice => {
    const existingDemographicPrice = pricingTerm?.demographicPrices?.find(
        (item) => item.template.id === lineItemTemplate.id,
    );

    if (existingDemographicPrice) {
        return existingDemographicPrice;
    }

    return {
        template: { id: lineItemTemplate.id },
    };
};

/**
 * This function will calculates the adjusted price for a demographicPrice model.
 * we need to pass the base price and based on adjustment amount, type the amount is calculated
 */
export const calculatePriceWithAdjustment = (basePrice: number, demographicPrice: DemographicPrice) => {
    if (isNil(basePrice) || isNil(demographicPrice.adjustmentAmount) || demographicPrice.adjustmentAmount < 0) {
        return null;
    }

    if (demographicPrice.adjustmentOperation === AdjustmentOperation.FINAL) {
        return demographicPrice.adjustmentAmount;
    }

    const isMarkdown = demographicPrice.adjustmentOperation === AdjustmentOperation.MARKDOWN;
    const calculatedAdjustment =
        demographicPrice.adjustmentType === AdjustmentType.ABSOLUTE
            ? demographicPrice.adjustmentAmount
            : round((Math.abs(demographicPrice.adjustmentAmount) * basePrice) / 100, 2);

    const finalPrice = basePrice + (isMarkdown ? -1 : 1) * calculatedAdjustment;
    return round(finalPrice, 2);
};
